import { Appender, LogEntry, Encoder } from './types'
import { StandardEncoder } from './encoders'

export class ConsoleAppender implements Appender {
  private encoder: Encoder

  constructor(encoder: Encoder = new StandardEncoder()) {
    this.encoder = encoder
  }

  async append(entry: LogEntry): Promise<void> {
    const formattedEntry = this.encoder.encode(entry)
    console[entry.level](formattedEntry)
  }
}

export class ServerAppender implements Appender {
  private batchedLogs: LogEntry[] = [];
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_INTERVAL = 5_000; // 5 seconds in milliseconds
  private readonly MAX_BATCH_SIZE = 100;
  private readonly MAX_RETRY_ATTEMPTS = 3;

  constructor() {}

  async append(entry: LogEntry): Promise<void> {
    this.batchedLogs.push(entry);

    // If we've reached max batch size, flush immediately
    if (this.batchedLogs.length >= this.MAX_BATCH_SIZE) {
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
        this.batchTimeout = null;
      }
      await this.flushLogs();
      return;
    }

    // Set up the normal timeout-based flush if not already set
    if (!this.batchTimeout) {
      this.batchTimeout = setTimeout(() => {
        void this.flushLogs();
      }, this.BATCH_INTERVAL);
    }
  }

  private async flushLogs(retryCount = 0): Promise<void> {
    if (this.batchedLogs.length === 0) return;

    const logsToSend = [...this.batchedLogs];
    this.batchedLogs = []; // Clear the batch
    this.batchTimeout = null;

    try {
      await fetch('/api/logs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logsToSend)
      });
    }
		catch (error) {
      if (retryCount < this.MAX_RETRY_ATTEMPTS) {
        // Wait using exponential backoff before retrying
        const backoffTime = Math.pow(2, retryCount) * 1_000;
        await new Promise(resolve => setTimeout(resolve, backoffTime));

        // Add logs back to the beginning of the batch
        this.batchedLogs = [...logsToSend, ...this.batchedLogs];

        // Retry with incremented retry count
        await this.flushLogs(retryCount + 1);
      }
			else {
        // If all retries failed, log locally
        console.error('Failed to send logs to server after all retry attempts. Logging locally:');
        logsToSend.forEach(log => {
          console.log('Failed to send log:', JSON.stringify(log));
        });

        // Also log the error that caused the failure
        console.error('Original error:', error);
      }
    }
  }

  // Method to manually flush logs (useful for application shutdown)
  async flush(): Promise<void> {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
      this.batchTimeout = null;
    }
    await this.flushLogs();
  }
}

export function createAppender(encoder: Encoder = new StandardEncoder()): Appender {
	if (typeof window === 'undefined') { // We're on the server, always use ConsoleAppender
    return new ConsoleAppender(encoder)
  }
	else if (process.env.NODE_ENV === 'production') {
		return new ServerAppender()  //no encoder, will be done by the receiver of the API content
	}
	return new ConsoleAppender(encoder)  //fallback is always safe for server or client
}